import React from 'react';
import Badge from '../ui/Badge';
import Icons from '../Icons';
import { formatDate } from '../safeguarding/safeguardingUtils';
import { getFollowUpStatus } from './incidentUtils';

const severityLabels = {
  critical: 'Critical',
  high: 'High',
  medium: 'Medium',
  low: 'Low'
};

const severityVariants = {
  critical: 'critical',
  high: 'high',
  medium: 'medium',
  low: 'low'
};

const RowMenu = ({ incident, onView, onEdit, onAssign, onClose }) => {
  return (
    <details className="sp-row-menu" onClick={(event) => event.stopPropagation()}>
      <summary aria-label={`Actions for incident ${incident.type}`}>
        <span aria-hidden="true">⋯</span>
      </summary>
      <div className="sp-row-menu__panel" role="menu">
        <button type="button" role="menuitem" onClick={() => onView?.(incident)}>
          View details
        </button>
        <button type="button" role="menuitem" onClick={() => onEdit?.(incident)}>
          Update incident
        </button>
        <button type="button" role="menuitem" onClick={() => onAssign?.(incident)}>
          Assign staff
        </button>
        {incident.status !== 'closed' && (
          <button type="button" role="menuitem" onClick={() => onClose?.(incident)}>
            Close incident
          </button>
        )}
      </div>
    </details>
  );
};

export default function IncidentsTable({
  incidents,
  participantLookup,
  selectedIds,
  onToggleSelect,
  onToggleSelectAll,
  onRowClick,
  onViewIncident,
  onEditIncident,
  onAssignIncident,
  onCloseIncident
}) {
  const visibleIds = incidents.map((incident) => incident.id);
  const allSelected = visibleIds.length > 0 && visibleIds.every((id) => selectedIds.has(id));

  if (!incidents.length) {
    return (
      <div className="sp-panel sp-panel--muted p-6 text-sm sp-muted">
        No incidents match the selected filters.
      </div>
    );
  }

  return (
    <div className="sp-table sp-table--incidents-workspace" role="table" aria-label="Incidents">
      <div className="sp-row sp-row--header" role="row">
        <div role="columnheader">
          <input
            type="checkbox"
            aria-label="Select all incidents"
            checked={allSelected}
            onChange={() => onToggleSelectAll(allSelected)}
          />
        </div>
        <div role="columnheader">Date</div>
        <div role="columnheader">Incident</div>
        <div role="columnheader">Severity</div>
        <div role="columnheader">Students</div>
        <div role="columnheader">Assigned to</div>
        <div role="columnheader">Follow-up</div>
        <div role="columnheader" className="text-right">Actions</div>
      </div>
      {incidents.map((incident) => {
        const severityKey = String(incident.severity || 'medium').toLowerCase();
        const followUpStatus = getFollowUpStatus(incident);
        const studentNames = (incident.participants || [])
          .filter((participant) => participant.type === 'student')
          .map((participant) => participant.name || participantLookup?.[participant.id] || participant.id)
          .join(', ');
        const assignedName = incident.assignedKeyWorker || '';
        const isSelected = selectedIds.has(incident.id);
        const isCritical = severityKey === 'critical';
        const dueLabel = incident.followUpDate ? formatDate(incident.followUpDate) : '';
        const shortDescription = incident.description
          ? incident.description.length > 80
            ? `${incident.description.slice(0, 80)}…`
            : incident.description
          : 'No description';
        return (
          <div
            key={incident.id}
            className={`sp-row sp-row--incident ${
              isCritical ? 'sp-row--critical' : ''
            } ${followUpStatus.isOverdue ? 'sp-row--followup-overdue' : ''} ${isSelected ? 'sp-row--selected' : ''}`}
            role="row"
            tabIndex={0}
            aria-selected={isSelected}
            onClick={() => onRowClick(incident)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                onRowClick(incident);
              }
            }}
          >
            <div role="cell" onClick={(event) => event.stopPropagation()}>
              <input
                type="checkbox"
                checked={isSelected}
                aria-label={`Select incident ${incident.type}`}
                onChange={() => onToggleSelect(incident.id)}
              />
            </div>
            <div role="cell" className="text-sm sp-muted">{formatDate(incident.date)}</div>
            <div role="cell">
              <div className="sp-value" style={{ fontWeight: 600 }}>
                {isCritical && <Icons.Alert />} {incident.type}
              </div>
              <div className="text-xs sp-muted">{shortDescription}</div>
            </div>
            <div role="cell">
              <Badge variant={severityVariants[severityKey] || 'medium'}>
                {severityLabels[severityKey] || incident.severity}
              </Badge>
            </div>
            <div role="cell" className="text-sm sp-muted">{studentNames || '—'}</div>
            <div role="cell" className="text-sm sp-muted">
              {assignedName ? assignedName : (
                <span className="sp-inline-warning">
                  <Icons.Alert /> Unassigned
                </span>
              )}
            </div>
            <div role="cell" className="text-sm sp-muted">
              {incident.followUpRequired ? (
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge variant={followUpStatus.isOverdue ? 'review' : followUpStatus.isDueSoon ? 'review' : 'inactive'}>
                    {followUpStatus.isOverdue ? <Icons.Alert /> : <Icons.Clock />}
                    {followUpStatus.isOverdue ? 'Overdue' : followUpStatus.isDueSoon ? 'Due soon' : 'Follow-up'}
                  </Badge>
                  {dueLabel && <span>{dueLabel}</span>}
                </div>
              ) : (
                <Badge variant="inactive">None</Badge>
              )}
            </div>
            <div role="cell" className="text-right">
              <RowMenu
                incident={incident}
                onView={onViewIncident}
                onEdit={onEditIncident}
                onAssign={onAssignIncident}
                onClose={onCloseIncident}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}
